import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin, of, timer, Subscription, type Observable } from 'rxjs';
import { catchError, map, switchMap, take, takeWhile } from 'rxjs/operators';
import { DataForSEOService, UrlAnalysisResponse } from '../../services/dataforseo';
import {
  PageComparisonService,
  PageComparisonResult,
  AnalysisStatusResponse,
  IncomingLink,
  DomainTopic
} from '../../services/page-comparison';
import { DomainTopicsService } from '../../services/domain-topics';
import {
  computeTopicFitData,
  emptyTopicFitData,
  extractDomainFromUrl,
  formatAnchorText,
  formatTopicTier,
  getLinkTypeClass,
  getLinkTypeLabel,
  getLinkTypeTooltip,
  getMainTopicFromAnalysis,
  getTopicSignalsCount,
  getTopicTierClass,
  linkTargetDisplayUrl,
  QuickCompareTopicFitData,
  resolveUrlIdFromAnalysis
} from '../../utils/quick-compare-topic-fit';
import {
  buildQuickCompareDiagnosisBullets,
  computeKeywordOverlapFromAnalyses,
  diagnosisHasCompetitorWins,
  diagnosisHasYourWins,
  getAnalyzedAtIso,
  getContentOpportunitiesFromOverlap,
  getCriticalIssues,
  getLastModifiedIso,
  type ContentOpportunity,
  type DiagnosisBullet,
  type KeywordOverlapAnalysis,
  rankedKeywordsNeverFetched
} from '../../utils/quick-compare-analysis';

type ColumnSide = 'primary' | 'compare';

interface ColumnState {
  status: 'loading' | 'analyzing' | 'completed' | 'error' | 'empty' | 'prompt';
  analysis: any | null;
  error: string | null;
  progress: { pagesCrawled: number; pagesInQueue: number } | null;
}

interface IncomingLinksColumnState {
  metadata: {
    totalIncomingLinks: number;
    contextualLinks: number;
    structuralLinks: number;
    samePageLinks: number;
    unknownLinks: number;
    requiresDomainCrawl: boolean;
    domain: string;
  } | null;
  links: IncomingLink[] | null;
  fullLoading: boolean;
  error: string | null;
  expanded: boolean;
  showAll: boolean;
}

interface SupportingPage {
  url: string;
  seoIntentScore: number;
  estimatedOrganicTraffic?: number;
}

interface SupportingTopic {
  topicSlug: string;
  topicLabel: string;
  yourPages: SupportingPage[];
  theirPages: SupportingPage[];
}

interface SupportingPagesState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  topics: SupportingTopic[];
  expanded: boolean;
}

@Component({
  selector: 'app-inventory-quick-compare-drawer',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './inventory-quick-compare-drawer.html',
  styleUrl: './inventory-quick-compare-drawer.css'
})
export class InventoryQuickCompareDrawerComponent implements OnChanges, OnDestroy {
  @Input() open = false;
  @Input() primaryUrl: string | null = null;
  @Input() compareUrl: string | null = null;
  @Input() primaryDomainLabel = '';
  @Input() compareDomainLabel = '';

  @Output() closed = new EventEmitter<void>();
  /** User chose to open Full Compare (Topic Share pipeline) on `/page-comparison`; parent navigates with URLs. */
  @Output() openFullCompare = new EventEmitter<void>();

  primaryColumn: ColumnState = this.initialColumn();
  compareColumn: ColumnState = this.initialColumn();

  primaryIncoming: IncomingLinksColumnState = this.initialIncoming();
  compareIncoming: IncomingLinksColumnState = this.initialIncoming();

  /** Synced: expand/collapse critical issues details (both columns). */
  issuesDetailsExpanded = false;
  keywordOpportunitiesExpanded = false;
  keywordDetailsExpanded = false;

  readonly internalLinksDisplayLimit = 25;

  /** Shown near ranked keywords / overlap / diagnosis when Labs keyword data was never stored. */
  readonly keywordFetchHint =
    'Quick Compare does not run Labs ranked-keyword fetches. Open Deep Compare and run Start Comparison there to fetch or refresh keyword rankings when you see "—" or missing overlap.';

  readonly keywordPartialHint =
    'Keyword overlap, opportunities, and some competitive signals need ranked keyword data. Use Deep Compare → Start Comparison to load or refresh Labs data for a fuller picture.';

  private domainTopicsCache = new Map<string, DomainTopic[]>();
  private domainTopicsLoading = new Map<string, boolean>();
  private domainTopicsError = new Map<string, string | null>();

  supportingPages: SupportingPagesState = { status: 'idle', topics: [], expanded: false };

  private loadRequestGen = 0;
  private loadSubscription = new Subscription();
  private priorBodyOverflow = '';

  constructor(
    private dataForSeo: DataForSEOService,
    private pageComparison: PageComparisonService,
    private domainTopicsService: DomainTopicsService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      if (this.open) {
        this.priorBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.startLoad();
      } else {
        document.body.style.overflow = this.priorBodyOverflow;
        this.teardownLoads();
      }
    } else if (this.open && (changes['primaryUrl'] || changes['compareUrl'])) {
      this.startLoad();
    }
  }

  ngOnDestroy(): void {
    this.teardownLoads();
    if (this.open) {
      document.body.style.overflow = this.priorBodyOverflow;
    }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.open || event.key !== 'Escape') return;
    event.preventDefault();
    this.emitClose();
  }

  emitClose(): void {
    this.closed.emit();
  }

  onOpenFullCompareClick(): void {
    const p = (this.primaryUrl || '').trim();
    const c = (this.compareUrl || '').trim();
    if (!p || !c) return;
    this.openFullCompare.emit();
  }

  get canOpenFullCompare(): boolean {
    return !!(this.primaryUrl || '').trim() && !!(this.compareUrl || '').trim();
  }

  pageTitle(analysis: any): string {
    return (
      analysis?.rawData?.pageData?.meta?.title ||
      analysis?.rawData?.summary?.title ||
      '—'
    );
  }

  rankedKeywordDisplayCount(analysis: any): string {
    const n = analysis?.rankedKeywords?.keywords?.length;
    const total = analysis?.rankedKeywords?.totalCount;
    if (typeof n === 'number' && n > 0) return String(n);
    if (typeof total === 'number' && total > 0) return String(total);
    return '—';
  }

  rankedKeywordsMissingNote(analysis: any): boolean {
    return rankedKeywordsNeverFetched(analysis);
  }

  formatEstTraffic(analysis: any): string {
    const v = analysis?.estimatedOrganicTraffic;
    if (!Number.isFinite(v) || v === null || v === undefined) return '—';
    if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
    return String(Math.round(v));
  }

  getAnalyzedAtIsoForSide(side: ColumnSide): string | null {
    const a = this.analysisForSide(side);
    return a ? getAnalyzedAtIso(a) : null;
  }

  getLastModifiedIsoForSide(side: ColumnSide): string | null {
    const a = this.analysisForSide(side);
    return a ? getLastModifiedIso(a) : null;
  }

  getCriticalIssuesForSide(side: ColumnSide): string[] {
    const a = this.analysisForSide(side);
    if (!a) return [];
    return getCriticalIssues(a);
  }

  getCriticalIssuesCountForSide(side: ColumnSide): number {
    return this.getCriticalIssuesForSide(side).length;
  }

  toggleIssuesDetails(): void {
    this.issuesDetailsExpanded = !this.issuesDetailsExpanded;
  }

  isIssuesDetailsExpanded(): boolean {
    return this.issuesDetailsExpanded;
  }

  toggleKeywordOpportunities(): void {
    this.keywordOpportunitiesExpanded = !this.keywordOpportunitiesExpanded;
  }

  isKeywordOpportunitiesExpanded(): boolean {
    return this.keywordOpportunitiesExpanded;
  }

  toggleKeywordDetails(): void {
    this.keywordDetailsExpanded = !this.keywordDetailsExpanded;
  }

  isKeywordDetailsExpanded(): boolean {
    return this.keywordDetailsExpanded;
  }

  get showKeywordDependencyCallout(): boolean {
    if (!this.bothColumnsCompleted) return false;
    return (
      rankedKeywordsNeverFetched(this.primaryColumn.analysis) ||
      rankedKeywordsNeverFetched(this.compareColumn.analysis)
    );
  }

  computeKeywordOverlap(): KeywordOverlapAnalysis | null {
    if (!this.bothColumnsCompleted) return null;
    return computeKeywordOverlapFromAnalyses(
      this.primaryColumn.analysis,
      this.compareColumn.analysis
    );
  }

  getKeywordOpportunities(): ContentOpportunity[] {
    return getContentOpportunitiesFromOverlap(this.computeKeywordOverlap());
  }

  getSharedKeywordsWithPositions(): Array<{
    keyword: string;
    yourPosition: number;
    competitorPosition: number;
    searchVolume?: number;
  }> {
    return this.computeKeywordOverlap()?.sharedKeywords || [];
  }

  getPrimaryUniqueKeywords(): Array<{ keyword: string; position: number; searchVolume?: number }> {
    return this.computeKeywordOverlap()?.yourUniqueKeywords || [];
  }

  getCompareUniqueKeywords(): Array<{ keyword: string; position: number; searchVolume?: number }> {
    return this.computeKeywordOverlap()?.competitorUniqueKeywords || [];
  }

  getDiagnosisBullets(): DiagnosisBullet[] {
    if (!this.bothColumnsCompleted) return [];
    return buildQuickCompareDiagnosisBullets({
      yourAnalysis: this.primaryColumn.analysis,
      competitorAnalysis: this.compareColumn.analysis,
      overlap: this.computeKeywordOverlap(),
      yourIncoming: { contextual: this.getIncomingLinksStats('primary').contextual },
      competitorIncoming: { contextual: this.getIncomingLinksStats('compare').contextual },
      yourTopicFit: this.topicFitForSide('primary'),
      competitorTopicFit: this.topicFitForSide('compare')
    });
  }

  hasCompetitorAdvantagesInDiagnosis(): boolean {
    return diagnosisHasCompetitorWins(this.getDiagnosisBullets());
  }

  hasYourAdvantagesInDiagnosis(): boolean {
    return diagnosisHasYourWins(this.getDiagnosisBullets());
  }

  topicSignalsCount(analysis: any): number {
    return getTopicSignalsCount(analysis);
  }

  /** When both sides have completed analysis, metrics use a synchronized row grid so titles/cards align. */
  get bothColumnsCompleted(): boolean {
    return (
      this.primaryColumn.status === 'completed' &&
      this.compareColumn.status === 'completed' &&
      !!this.primaryColumn.analysis &&
      !!this.compareColumn.analysis
    );
  }

  analysisForSide(side: ColumnSide): any {
    return side === 'primary' ? this.primaryColumn.analysis : this.compareColumn.analysis;
  }

  topicFitForSide(side: ColumnSide): QuickCompareTopicFitData {
    const col = side === 'primary' ? this.primaryColumn : this.compareColumn;
    const url = (side === 'primary' ? this.primaryUrl : this.compareUrl) || '';
    if (col.status !== 'completed' || !col.analysis) {
      return emptyTopicFitData();
    }
    const mainTopic = getMainTopicFromAnalysis(col.analysis);
    const domain = extractDomainFromUrl(url.trim());
    const normalizedDomain = domain.toLowerCase().trim();
    if (!normalizedDomain) {
      return emptyTopicFitData();
    }
    const loading = this.domainTopicsLoading.get(normalizedDomain) || false;
    const error = this.domainTopicsError.get(normalizedDomain) ?? null;
    const hasKey = this.domainTopicsCache.has(normalizedDomain);
    const parentTopics = hasKey ? this.domainTopicsCache.get(normalizedDomain) : undefined;
    return computeTopicFitData({
      mainTopic,
      parentTopics,
      domainTopicsLoading: loading,
      domainTopicsError: error
    });
  }

  formatTopicTierPublic(tier: QuickCompareTopicFitData['tier']): string {
    return formatTopicTier(tier);
  }

  topicTierClass(tier: QuickCompareTopicFitData['tier']): string {
    return getTopicTierClass(tier);
  }

  linkTypeLabel(t: string | undefined): string {
    return getLinkTypeLabel(t);
  }

  linkTypeTooltip(t: string | undefined): string {
    return getLinkTypeTooltip(t);
  }

  linkTypeClass(t: string | undefined): string {
    return getLinkTypeClass(t);
  }

  anchorText(t: string | undefined): string {
    return formatAnchorText(t);
  }

  targetUrlForAnalysis(analysis: any): string {
    return linkTargetDisplayUrl(analysis);
  }

  incomingFor(side: ColumnSide): IncomingLinksColumnState {
    return side === 'primary' ? this.primaryIncoming : this.compareIncoming;
  }

  getIncomingLinksCount(side: ColumnSide): number {
    const incoming = this.incomingFor(side);
    if (incoming.links !== null) {
      return incoming.links.length;
    }
    return incoming.metadata?.totalIncomingLinks || 0;
  }

  getIncomingLinksStats(side: ColumnSide): {
    total: number;
    contextual: number;
    structural: number;
    samePage: number;
    unknown: number;
  } {
    const incoming = this.incomingFor(side);
    if (incoming.links === null && incoming.metadata) {
      const meta = incoming.metadata;
      return {
        total: meta.totalIncomingLinks || 0,
        contextual: meta.contextualLinks || 0,
        structural: meta.structuralLinks || 0,
        samePage: meta.samePageLinks || 0,
        unknown: meta.unknownLinks || 0
      };
    }
    const links = incoming.links || [];
    return {
      total: links.length,
      contextual: links.filter((l) => l.linkType === 'contextual').length,
      structural: links.filter((l) => l.linkType === 'structural').length,
      samePage: links.filter((l) => l.linkType === 'same_page').length,
      unknown: links.filter((l) => !l.linkType || l.linkType === 'unknown').length
    };
  }

  getDisplayedIncomingLinks(side: ColumnSide): IncomingLink[] {
    const incoming = this.incomingFor(side);
    const links = incoming.links || [];
    const filtered = links.filter(
      (link) => link.linkType === 'contextual' || link.linkType === 'same_page'
    );
    if (incoming.showAll) {
      return filtered;
    }
    return filtered.slice(0, this.internalLinksDisplayLimit);
  }

  hasMoreIncomingLinks(side: ColumnSide): boolean {
    const incoming = this.incomingFor(side);
    const links = incoming.links || [];
    const filtered = links.filter(
      (link) => link.linkType === 'contextual' || link.linkType === 'same_page'
    );
    return !incoming.showAll && filtered.length > this.internalLinksDisplayLimit;
  }

  shouldShowIncomingLinksWarning(side: ColumnSide): boolean {
    const meta = this.incomingFor(side).metadata;
    if (!meta) return false;
    return meta.requiresDomainCrawl === true;
  }

  toggleIncomingExpanded(side: ColumnSide): void {
    const incoming = this.incomingFor(side);
    incoming.expanded = !incoming.expanded;
    if (incoming.expanded && incoming.links === null && !incoming.fullLoading) {
      this.loadIncomingFull(side);
    }
  }

  isIncomingExpanded(side: ColumnSide): boolean {
    return this.incomingFor(side).expanded;
  }

  toggleShowAllIncomingLinks(side: ColumnSide): void {
    const incoming = this.incomingFor(side);
    incoming.showAll = !incoming.showAll;
  }

  incomingExpandDisabled(side: ColumnSide): boolean {
    return this.incomingFor(side).fullLoading;
  }

  private initialColumn(): ColumnState {
    return {
      status: 'loading',
      analysis: null,
      error: null,
      progress: null
    };
  }

  private initialIncoming(): IncomingLinksColumnState {
    return {
      metadata: null,
      links: null,
      fullLoading: false,
      error: null,
      expanded: false,
      showAll: false
    };
  }

  private resetColumns(): void {
    this.primaryColumn = this.initialColumn();
    this.compareColumn = this.initialColumn();
    this.resetAllEnrichment();
  }

  private resetAllEnrichment(): void {
    this.primaryIncoming = this.initialIncoming();
    this.compareIncoming = this.initialIncoming();
    this.domainTopicsCache.clear();
    this.domainTopicsLoading.clear();
    this.domainTopicsError.clear();
    this.supportingPages = { status: 'idle', topics: [], expanded: false };
    this.issuesDetailsExpanded = false;
    this.keywordOpportunitiesExpanded = false;
    this.keywordDetailsExpanded = false;
  }

  private resetColumnEnrichment(side: ColumnSide): void {
    if (side === 'primary') {
      this.primaryIncoming = this.initialIncoming();
    } else {
      this.compareIncoming = this.initialIncoming();
    }
  }

  private teardownLoads(): void {
    this.loadSubscription.unsubscribe();
    this.loadSubscription = new Subscription();
  }

  private startLoad(): void {
    this.teardownLoads();
    const bucket = this.loadSubscription;
    const gen = ++this.loadRequestGen;
    this.resetColumns();

    const primary = (this.primaryUrl || '').trim();
    const compare = (this.compareUrl || '').trim();
    if (!primary || !compare) {
      if (!primary) {
        this.primaryColumn = {
          status: 'error',
          analysis: null,
          error: 'Missing URL for this column.',
          progress: null
        };
      }
      if (!compare) {
        this.compareColumn = {
          status: 'error',
          analysis: null,
          error: 'Missing URL for this column.',
          progress: null
        };
      }
      return;
    }

    const sub = forkJoin({
      primary: this.safeGetAnalysis(primary),
      compare: this.safeGetAnalysis(compare)
    }).subscribe({
      next: ({ primary: pRes, compare: cRes }) => {
        if (gen !== this.loadRequestGen) return;

        // Handle any GET failures before proceeding. 404 = not yet in DB → show
        // a confirm prompt; any other error → hard error state.
        if (!pRes.ok || !cRes.ok) {
          if (!pRes.ok) {
            this.primaryColumn = pRes.error.status === 404
              ? { status: 'prompt', analysis: null, error: null, progress: null }
              : { status: 'error', analysis: null, error: this.formatHttpError(pRes.error), progress: null };
          }
          if (!cRes.ok) {
            this.compareColumn = cRes.error.status === 404
              ? { status: 'prompt', analysis: null, error: null, progress: null }
              : { status: 'error', analysis: null, error: this.formatHttpError(cRes.error), progress: null };
          }
          // Apply success to whichever side did load normally.
          if (pRes.ok) { this.applyGetSuccess('primary', pRes.data); }
          if (cRes.ok) { this.applyGetSuccess('compare', cRes.data); }
          return;
        }

        // Both GETs succeeded — TypeScript now knows both are ok: true.
        const pNeed = this.needsRefreshFromGet(pRes.data);
        const cNeed = this.needsRefreshFromGet(cRes.data);

        if (!pNeed && !cNeed) {
          this.applyGetSuccess('primary', pRes.data);
          this.applyGetSuccess('compare', cRes.data);
          return;
        }

        this.runComparePost(primary, compare, gen);
      },
      error: () => {
        if (gen !== this.loadRequestGen) return;
        this.primaryColumn = {
          status: 'error',
          analysis: null,
          error: 'Could not load comparison.',
          progress: null
        };
        this.compareColumn = {
          status: 'error',
          analysis: null,
          error: 'Could not load comparison.',
          progress: null
        };
      }
    });

    bucket.add(sub);
  }

  /**
   * User confirmed they want to analyze a URL that wasn't yet in the database.
   * Both sides must either be completed/fresh or confirmed before the POST runs.
   */
  confirmAnalysis(side: ColumnSide): void {
    const gen = this.loadRequestGen;
    const primary = (this.primaryUrl || '').trim();
    const compare = (this.compareUrl || '').trim();
    if (!primary || !compare) return;

    // Mark the confirmed side as loading so the spinner shows.
    this.setColumn(side, { status: 'loading', analysis: null, error: null, progress: null }, gen);

    // Only fire the POST once both sides are ready to proceed (not in prompt state).
    const pStatus = this.primaryColumn.status;
    const cStatus = this.compareColumn.status;
    const otherReady = side === 'primary'
      ? cStatus !== 'prompt'
      : pStatus !== 'prompt';

    if (otherReady) {
      this.runComparePost(primary, compare, gen);
    }
    // If the other side is still in prompt state, the POST will fire when they confirm theirs.
  }

  private runComparePost(primary: string, compare: string, gen: number): void {
    const postSub = this.pageComparison
      .comparePages(primary, compare, { skipRankedKeywords: true })
      .subscribe({
        next: (comparisonResp) => {
          if (gen !== this.loadRequestGen) return;
          this.applyCompareResult(comparisonResp.data.yourPage, 'primary', gen);
          this.applyCompareResult(comparisonResp.data.competitorPage, 'compare', gen);
          const y = comparisonResp.data.yourPage;
          const comp = comparisonResp.data.competitorPage;
          if (y.status === 'pending' && y.taskId) {
            this.startPoll(y.taskId, 'primary', gen);
          }
          if (comp.status === 'pending' && comp.taskId) {
            this.startPoll(comp.taskId, 'compare', gen);
          }
        },
        error: (err: HttpErrorResponse) => {
          if (gen !== this.loadRequestGen) return;
          const msg = this.formatHttpError(err);
          this.setColumn('primary', { status: 'error', analysis: null, error: msg, progress: null }, gen);
          this.setColumn('compare', { status: 'error', analysis: null, error: msg, progress: null }, gen);
        }
      });
    this.loadSubscription.add(postSub);
  }

  private safeGetAnalysis(
    url: string
  ): Observable<
    | { ok: true; data: UrlAnalysisResponse }
    | { ok: false; error: HttpErrorResponse }
  > {
    return this.dataForSeo.getUrlAnalysis(url).pipe(
      map((data) => ({ ok: true as const, data })),
      catchError((error: HttpErrorResponse) => of({ ok: false as const, error }))
    );
  }

  private needsRefreshFromGet(res: UrlAnalysisResponse): boolean {
    const d = res.data;
    if (d.status !== 'completed' || !d.analysis) return true;
    return !this.isAnalysisFresh(d.analysis);
  }

  /** True when stored PageAnalysis exists (any age — crawl/post-compare data is reused). */
  private isAnalysisFresh(analysis: any): boolean {
    const at = analysis?.analyzedAt;
    if (!at) return false;
    const analyzed = new Date(at);
    return !Number.isNaN(analyzed.getTime());
  }

  private applyGetSuccess(side: ColumnSide, res: UrlAnalysisResponse): void {
    const d = res.data;
    if (d.status === 'completed' && d.analysis) {
      this.setColumn(
        side,
        {
          status: 'completed',
          analysis: d.analysis,
          error: null,
          progress: null
        },
        this.loadRequestGen
      );
    } else {
      this.setColumn(
        side,
        {
          status: 'empty',
          analysis: null,
          error: null,
          progress: null
        },
        this.loadRequestGen
      );
    }
  }

  private applyCompareResult(result: PageComparisonResult, side: ColumnSide, gen: number): void {
    if (result.status === 'completed' && result.analysis) {
      this.setColumn(
        side,
        {
          status: 'completed',
          analysis: result.analysis,
          error: null,
          progress: null
        },
        gen
      );
    } else if (result.status === 'pending' && result.taskId) {
      this.setColumn(
        side,
        {
          status: 'analyzing',
          analysis: null,
          error: null,
          progress: null
        },
        gen
      );
    } else {
      this.setColumn(
        side,
        {
          status: 'error',
          analysis: null,
          error: 'Unexpected response when refreshing analysis.',
          progress: null
        },
        gen
      );
    }
  }

  private setColumn(side: ColumnSide, state: ColumnState, gen: number): void {
    if (side === 'primary') {
      this.primaryColumn = state;
    } else {
      this.compareColumn = state;
    }

    if (state.status === 'completed' && state.analysis) {
      this.startColumnEnrichment(side, state.analysis, gen);
    } else {
      this.resetColumnEnrichment(side);
    }

    if (this.bothColumnsCompleted && this.supportingPages.status === 'idle') {
      this.loadSupportingPages(gen);
    }
  }

  private startColumnEnrichment(side: ColumnSide, analysis: any, gen: number): void {
    this.resetColumnEnrichment(side);
    const urlForSide = ((side === 'primary' ? this.primaryUrl : this.compareUrl) || '').trim();
    const domain = extractDomainFromUrl(urlForSide);

    if (getTopicSignalsCount(analysis) > 0 && domain) {
      this.ensureDomainTopicsLoaded(domain, gen);
    }

    const urlId = resolveUrlIdFromAnalysis(analysis);
    const incoming = side === 'primary' ? this.primaryIncoming : this.compareIncoming;

    if (!urlId) {
      incoming.error = 'URL ID not available in analysis';
      return;
    }

    const sub = this.pageComparison
      .getIncomingLinks(urlId, { summaryOnly: true })
      .pipe(
        take(1),
        catchError(() => of(null))
      )
      .subscribe((response) => {
        if (gen !== this.loadRequestGen) return;
        if (response?.success) {
          incoming.metadata = response.data.metadata;
        }
      });
    this.loadSubscription.add(sub);
  }

  private loadSupportingPages(gen: number): void {
    const primaryAnalysis = this.primaryColumn.analysis;
    const compareAnalysis = this.compareColumn.analysis;
    if (!primaryAnalysis || !compareAnalysis) return;

    const primaryDomain = extractDomainFromUrl((this.primaryUrl || '').trim());
    const compareDomain = extractDomainFromUrl((this.compareUrl || '').trim());
    if (!primaryDomain || !compareDomain) return;

    const getTopTopics = (analysis: any): Array<{ slug: string; label: string; weight: number }> => {
      const signals: any[] = Array.isArray(analysis?.topic?.topicSignals) ? analysis.topic.topicSignals : [];
      return signals
        .filter((s: any) => Number.isFinite(s?.weight) && Number(s.weight) >= 0.3 && s?.topicSlug)
        .sort((a: any, b: any) => Number(b.weight) - Number(a.weight))
        .slice(0, 3)
        .map((s: any) => ({ slug: String(s.topicSlug), label: String(s.label || s.topicSlug), weight: Number(s.weight) }));
    };

    const primaryTopics = getTopTopics(primaryAnalysis);
    const compareTopics = getTopTopics(compareAnalysis);

    const seenSlugs = new Set<string>();
    const allTopicSlugs: Array<{ slug: string; label: string }> = [];
    for (const t of [...primaryTopics, ...compareTopics]) {
      if (!seenSlugs.has(t.slug)) {
        seenSlugs.add(t.slug);
        allTopicSlugs.push({ slug: t.slug, label: t.label });
      }
    }

    if (allTopicSlugs.length === 0) {
      this.supportingPages = { status: 'ready', topics: [], expanded: false };
      return;
    }

    this.supportingPages = { status: 'loading', topics: [], expanded: false };

    const diagnosticCalls = allTopicSlugs.slice(0, 6).map(({ slug, label }) =>
      forkJoin({
        primary: this.domainTopicsService.getTopicDiagnostic(primaryDomain, slug).pipe(catchError(() => of(null))),
        compare: this.domainTopicsService.getTopicDiagnostic(compareDomain, slug).pipe(catchError(() => of(null))),
        meta: of({ slug, label })
      })
    );

    const sub = forkJoin(diagnosticCalls).pipe(
      map((results) => {
        const extractPages = (res: any): SupportingPage[] => {
          if (!res?.data?.coverage?.directlyCoveredTopics) return [];
          const topics: any[] = res.data.coverage.directlyCoveredTopics;
          const allUrls: SupportingPage[] = [];
          for (const t of topics) {
            const pages = [
              ...(Array.isArray(t.corePages) ? t.corePages : []),
              ...(Array.isArray(t.mentionPages) ? t.mentionPages : [])
            ];
            for (const p of pages) {
              if (p?.url) {
                allUrls.push({
                  url: p.url,
                  seoIntentScore: Number.isFinite(p.seoIntentScore) ? p.seoIntentScore : 0,
                  estimatedOrganicTraffic: typeof p.estimatedOrganicTraffic === 'number' ? p.estimatedOrganicTraffic : undefined
                });
              }
            }
          }
          return allUrls.sort((a, b) => b.seoIntentScore - a.seoIntentScore).slice(0, 5);
        };

        return results.map((r) => ({
          topicSlug: r.meta.slug,
          topicLabel: r.meta.label,
          yourPages: extractPages(r.primary),
          theirPages: extractPages(r.compare)
        } as SupportingTopic));
      }),
      catchError(() => of([] as SupportingTopic[]))
    ).subscribe((topics) => {
      if (gen !== this.loadRequestGen) return;
      this.supportingPages = {
        status: 'ready',
        topics: topics.filter((t) => t.yourPages.length > 0 || t.theirPages.length > 0),
        expanded: false
      };
    });

    this.loadSubscription.add(sub);
  }

  toggleSupportingPages(): void {
    this.supportingPages = { ...this.supportingPages, expanded: !this.supportingPages.expanded };
  }

  formatSupportingTraffic(v: number | undefined): string {
    if (!Number.isFinite(v) || v === undefined || v === null) return '';
    if (v >= 1000) return `~${(v / 1000).toFixed(1)}k/mo`;
    return `~${Math.round(v)}/mo`;
  }

  formatSupportingUrl(url: string): string {
    try {
      const parsed = new URL(url);
      return parsed.pathname || '/';
    } catch {
      return url;
    }
  }

  private ensureDomainTopicsLoaded(domain: string, gen: number): void {
    const normalizedDomain = domain.toLowerCase().trim();
    if (!normalizedDomain) return;

    if (this.domainTopicsCache.has(normalizedDomain)) {
      return;
    }
    if (this.domainTopicsLoading.get(normalizedDomain)) {
      return;
    }

    this.domainTopicsLoading.set(normalizedDomain, true);
    this.domainTopicsError.set(normalizedDomain, null);

    const sub = this.pageComparison.getDomainTopics(normalizedDomain).pipe(take(1)).subscribe({
      next: (response) => {
        if (gen !== this.loadRequestGen) return;
        this.domainTopicsLoading.set(normalizedDomain, false);
        if (response?.success && Array.isArray(response.data?.parentTopics)) {
          this.domainTopicsCache.set(normalizedDomain, response.data.parentTopics);
        } else {
          this.domainTopicsCache.set(normalizedDomain, []);
        }
      },
      error: (error: any) => {
        if (gen !== this.loadRequestGen) return;
        this.domainTopicsLoading.set(normalizedDomain, false);
        if (error.status === 404) {
          this.domainTopicsCache.set(normalizedDomain, []);
          this.domainTopicsError.set(normalizedDomain, null);
        } else {
          this.domainTopicsError.set(
            normalizedDomain,
            error.error?.message || error.message || 'Failed to load domain topics'
          );
        }
      }
    });
    this.loadSubscription.add(sub);
  }

  private loadIncomingFull(side: ColumnSide): void {
    const col = side === 'primary' ? this.primaryColumn : this.compareColumn;
    const incoming = this.incomingFor(side);
    if (col.status !== 'completed' || !col.analysis) return;
    const urlId = resolveUrlIdFromAnalysis(col.analysis);
    if (!urlId) return;
    const gen = this.loadRequestGen;
    incoming.fullLoading = true;
    incoming.error = null;
    const sub = this.pageComparison
      .getIncomingLinks(urlId)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (gen !== this.loadRequestGen) return;
          incoming.fullLoading = false;
          if (response?.success) {
            incoming.links = response.data.incomingLinks;
            incoming.metadata = response.data.metadata;
          } else {
            incoming.error = 'Failed to load incoming links';
            incoming.links = [];
          }
        },
        error: (err: HttpErrorResponse) => {
          if (gen !== this.loadRequestGen) return;
          incoming.fullLoading = false;
          incoming.error = err.error?.message || err.message || 'Failed to load incoming links';
          incoming.links = [];
        }
      });
    this.loadSubscription.add(sub);
  }

  private startPoll(taskId: string, side: ColumnSide, gen: number): void {
    const pollSub = timer(0, 5000)
      .pipe(
        switchMap(() => this.pageComparison.getAnalysisStatus(taskId)),
        takeWhile(
          (response: AnalysisStatusResponse) => response.data?.status === 'pending',
          true
        ),
        catchError((error) => {
          this.setColumn(
            side,
            {
              status: 'error',
              analysis: null,
              error: error?.message || 'Failed to check analysis status.',
              progress: null
            },
            gen
          );
          return of(null as AnalysisStatusResponse | null);
        })
      )
      .subscribe((response) => {
        if (!response || gen !== this.loadRequestGen) return;
        const status = response.data?.status;
        if (status === 'pending') {
          const p = response.data?.progress;
          const col = side === 'primary' ? this.primaryColumn : this.compareColumn;
          if (p) {
            if (side === 'primary') {
              this.primaryColumn = {
                ...col,
                status: 'analyzing',
                progress: {
                  pagesCrawled: p.pagesCrawled || 0,
                  pagesInQueue: p.pagesInQueue || 0
                }
              };
            } else {
              this.compareColumn = {
                ...col,
                status: 'analyzing',
                progress: {
                  pagesCrawled: p.pagesCrawled || 0,
                  pagesInQueue: p.pagesInQueue || 0
                }
              };
            }
          }
          return;
        }
        if (status === 'completed' && response.data?.analysis) {
          this.setColumn(
            side,
            {
              status: 'completed',
              analysis: response.data.analysis,
              error: null,
              progress: null
            },
            gen
          );
        } else if (status === 'failed') {
          this.setColumn(
            side,
            {
              status: 'error',
              analysis: null,
              error: response.data?.error || response.data?.message || 'Analysis failed.',
              progress: null
            },
            gen
          );
        }
      });

    this.loadSubscription.add(pollSub);
  }

  private formatHttpError(err: HttpErrorResponse): string {
    const msg = err?.error?.message || err?.message;
    return typeof msg === 'string' && msg.trim() ? msg : 'Could not load analysis for this URL.';
  }
}
